import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { totalOrBlank } from '@/features/publicsite/catalogCounts';
import type { HomeHeroProps } from '@/features/publicsite/types/home/HomeHeroProps';
import { useSession } from '@/app/session';
import { publicLayout } from '@/i18n/dictionaries/publicLayout';
import { publicHome, type PublicHomeKey } from '@/i18n/dictionaries/publicHome';
import { useTranslation } from '@/i18n/useTranslation';

const ROLE_CHIPS: PublicHomeKey[] = [
  'roleFarmers',
  'roleAdvisors',
  'roleChambers',
  'roleCcar',
  'roleResearch',
  'roleTraining',
  'roleInputs',
];

/**
 * The hero, and the account panel beside it.
 *
 * `counts` arrives as a prop rather than from its own {@link useCatalogCounts} call: the same
 * three totals are printed by the catalogue grid and the closing figures, and one request answers
 * all three — which is what the shared Thymeleaf fragment did before the React port.
 *
 * `heroImage` is the administered picture (/app/admin/acasa) or the static one: HomePage reads the
 * section once for this picture and for the focus strip below.
 *
 * The search field's state lives here because nothing outside this section reads it. It is the
 * only piece of state on the whole page that is not a loaded list.
 */
export function HomeHero({ counts, heroImage }: HomeHeroProps) {
  const { t } = useTranslation(publicHome);
  const { t: tch } = useTranslation(publicLayout);
  const { user } = useSession();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const num = (n: number | undefined) => totalOrBlank(counts, n);

  return (
    <section className="hero">
      <img className="hero__aerial" src={heroImage} alt="" aria-hidden="true" />
      <div className="container hero__grid">
        <div>
          <span className="hero__chip">{t('heroChip')}</span>
          <h1>{t('heroHeading')}</h1>
          <p className="hero__lead">{t('heroLead')}</p>
          <div className="hero__actions">
            <Link className="btn btn--primary btn--lg" to="/consilieri">
              {t('heroFindAdvisor')}
            </Link>
            <Link className="btn btn--outline btn--lg" to="/pagini/despre">
              {t('heroExplore')}
            </Link>
          </div>
          <div className="hero__stats" aria-label={t('heroStatsAria')}>
            <div className="hero__stat">
              <strong>{num(counts?.advisors)}</strong>
              <span>{t('heroStatAdvisors')}</span>
            </div>
            <div className="hero__stat">
              <strong>{num(counts?.trainings)}</strong>
              <span>{t('heroStatTrainings')}</span>
            </div>
            <div className="hero__stat">
              <strong>{num(counts?.library)}</strong>
              <span>{t('heroStatLibrary')}</span>
            </div>
          </div>
          <form
            className="hero-search"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              navigate(`/cautare?q=${encodeURIComponent(query)}`);
            }}
          >
            <label htmlFor="hero-q">{t('heroSearchLabel')}</label>
            <div className="hero-search__bar">
              <input
                id="hero-q"
                name="q"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('heroSearchPlaceholder')}
              />
              <button className="btn btn--primary btn--md" type="submit">
                {t('heroSearchButton')}
              </button>
            </div>
            <div className="chip-row">
              <Link className="chip" to="/biblioteca">
                {t('chipGuides')}
              </Link>
              <Link className="chip" to="/consilieri">
                {t('chipAdvisors')}
              </Link>
              <Link className="chip" to="/instruiri">
                {t('chipTrainings')}
              </Link>
              <Link className="chip" to="/cercetare">
                {t('chipResearch')}
              </Link>
              <Link className="chip" to="/finantare">
                {t('chipFunding')}
              </Link>
            </div>
          </form>
        </div>

        <aside className="account-card">
          <h2>{t('accountHeading')}</h2>
          {user ? (
            <>
              <p>
                {t('accountSignedIn')} <strong>{user.fullName}</strong>.
              </p>
              {/* A router link: the cabinet is the same React app under /app. */}
              <Link className="btn btn--primary btn--lg" to="/app/cabinet">
                {t('accountMyCabinet')}
              </Link>
            </>
          ) : (
            <>
              <p>{t('accountAnonLead')}</p>
              {/* A real navigation — the SAML entry point is a server redirect chain. There is no
                  separate sign-up: the account is created at the first M-Pass sign-in. */}
              <a className="btn btn--primary btn--lg" href="/saml/authenticate/mpass">
                <img className="btn__logo" src="/img/gov/mpass.svg" alt="" aria-hidden="true" />
                <span>{tch('navMpass')}</span>
              </a>
            </>
          )}
          <div className="account-card__roles">
            <h3>{t('accountRolesHeading')}</h3>
            <div className="account-card__chips">
              {ROLE_CHIPS.map((k) => (
                <span key={k}>{t(k)}</span>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
